import React, { useEffect, useState, useRef } from 'react';
import {
  ExternalLink,
  Laptop,
  GitBranch,
  HelpCircle,
  ChevronDown,
  Maximize2,
  LayoutTemplate
} from "lucide-react";
// Updated import: import permissionApi (default export) and use its checkPermission method.
import permissionApi from '../../utils/api/permissions/api';
import { ASSETS } from '../../constants';

const AppCard = ({ app, onVersionSwitch, onLaunch }) => {
  const [showVersionMenu, setShowVersionMenu] = useState(false);
  const [hasAccess, setHasAccess] = useState(true); // Assume access allowed by default
  const [accessCheckLoading, setAccessCheckLoading] = useState(false);
  const [accessDeniedReason, setAccessDeniedReason] = useState('');
  const menuRef = useRef(null);

  const hasProductionVersion = !!app.versions?.production?.component;
  const hasBetaVersion = !!app.versions?.beta?.component;
  const hasVersionChoice = hasProductionVersion && hasBetaVersion;
  const isBetaActive = app.selectedVersion === 'beta' || (!hasProductionVersion && hasBetaVersion);
  const displayMode = (app.display_mode || 'WINDOWED').toLowerCase();

  // Permission checks bypassed - all plugins publicly accessible
  // To re-enable, restore the permissionApi.checkPermission() call here

  const handleRequestAccess = (e) => {
    e.stopPropagation();
    console.log(`Request access for app ${app.id} submitted.`);
    // Future: open modal dialog from <AccessRequestDialog />
  };

  const handleClick = () => {
    if (hasAccess) {
      onLaunch();
    } else {
      console.warn(`User lacks permission for app ${app.id}: ${accessDeniedReason}`);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowVersionMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div
      onClick={handleClick}
      className={`group relative bg-gray-900 overflow-hidden border transition-all duration-200 hover:-translate-y-0.5 shadow-lg hover:shadow-2xl app-card cursor-pointer ${isBetaActive ? 'border-amber-500/50' : 'border-gray-800'}`}
    >
      <div className="absolute top-2 right-2 md:top-4 md:right-4 flex items-center space-x-1 md:space-x-2 z-10">
        {app.url && app.plugin_type === 'WEB' && (
          <a
            href={app.url}
            target="_blank"
            rel="noopener noreferrer"
            className="p-1.5 hover:bg-gray-800 rounded-full transition-colors bg-gray-900/80"
            title="View Source"
            onClick={e => e.stopPropagation()}
          >
            <GitBranch className="w-4 h-4 text-gray-400 hover:text-gray-300" />
          </a>
        )}
        {app.wikiUrl && (
          <a
            href={app.wikiUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="p-1.5 hover:bg-gray-800 rounded-full transition-colors bg-gray-900/80"
            title="View Documentation"
            onClick={e => e.stopPropagation()}
          >
            <HelpCircle className="w-4 h-4 text-gray-400 hover:text-gray-300" />
          </a>
        )}
      </div>

      <div className="p-3 md:p-6">
        {/* Mobile Layout: Title above, icon floats left, text wraps around */}
        <div className="md:hidden">
          {/* Title row */}
          <div className="flex items-center mb-2">
            <h2 className="text-base font-semibold text-gray-100 truncate group-hover:text-white">
              {app.name}
            </h2>
            {app.plugin_type === 'WEB' && (
              <div className={`ml-2 text-xs px-1.5 py-0.5 rounded-full font-normal ${isBetaActive ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20' : 'bg-green-500/10 text-green-400 border border-green-500/20'}`}>
                {isBetaActive ? 'beta' : 'prod'}
              </div>
            )}
          </div>
          {/* Floating icon with text wrap */}
          <img
            src={app.icon}
            alt={`${app.name} icon`}
            className="w-20 h-20 float-left mr-3 mb-2 object-cover bg-gray-800 rounded"
            onError={(e) => { e.target.src = ASSETS.IMAGES.PLACEHOLDER; }}
          />
          {app.author && (
            <p className="text-xs text-gray-400 mb-1">by {app.author}</p>
          )}
          <p className="text-sm text-gray-400 group-hover:text-gray-300">
            {app.description}
          </p>
          <div className="clear-both" />
        </div>

        {/* Desktop Layout: Horizontal flex */}
        <div className="hidden md:flex items-start space-x-6">
          <div className="flex-shrink-0 relative w-40 h-40">
            <img
              src={app.icon}
              alt={`${app.name} icon`}
              className="w-full h-full object-cover bg-gray-800 rounded"
              onError={(e) => { e.target.src = ASSETS.IMAGES.PLACEHOLDER; }}
            />
          </div>
          <div className="flex-1 min-w-0">
            <div className="space-y-1">
              <div className="flex items-center">
                <h2 className="text-xl font-semibold text-gray-100 truncate group-hover:text-white">
                  {app.name}
                </h2>
                {app.plugin_type === 'WEB' && (
                  <div className={`ml-2 text-xs px-2 py-0.5 rounded-full font-normal ${isBetaActive ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20' : 'bg-green-500/10 text-green-400 border border-green-500/20'}`}>
                    {isBetaActive ? 'beta' : 'production'}
                  </div>
                )}
              </div>
              {app.author && (
                <p className="text-sm text-gray-400">by {app.author}</p>
              )}
            </div>
            <p className="mt-3 text-base text-gray-400 group-hover:text-gray-300">
              {app.description}
            </p>
          </div>
        </div>
      </div>

      <div className={`px-3 md:px-6 py-2 md:py-3 ${isBetaActive ? 'bg-amber-950/10' : 'bg-gray-800'} text-sm font-medium border-t border-gray-800`}>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center space-x-2 md:space-x-3">
            <div className={`text-xs px-2 md:px-3 py-1 rounded-full flex items-center space-x-1 md:space-x-2 ${app.plugin_type === 'WEB' ? 'bg-blue-500/10 text-blue-400 border border-blue-500/20' : 'bg-purple-500/10 text-purple-400 border border-purple-500/20'}`}>
              {app.plugin_type === 'WEB' ? (
                <>
                  <Laptop className="w-3.5 h-3.5" />
                  <span>Web App</span>
                </>
              ) : (
                <>
                  <ExternalLink className="w-3.5 h-3.5" />
                  <span>Link</span>
                </>
              )}
            </div>
            {app.plugin_type === 'WEB' && (
              <div className="text-xs px-2 md:px-3 py-1 rounded-full flex items-center space-x-1 md:space-x-2 bg-gray-700/50 text-gray-300 border border-gray-600/50">
                {displayMode === 'fullscreen' ? (
                  <>
                    <Maximize2 className="w-3.5 h-3.5" />
                    <span>Fullscreen</span>
                  </>
                ) : (
                  <>
                    <LayoutTemplate className="w-3.5 h-3.5" />
                    <span>Windowed</span>
                  </>
                )}
              </div>
            )}
          </div>
          <div className="flex items-center space-x-2">
            {accessCheckLoading ? (
              <div className="text-xs text-gray-400">Checking...</div>
            ) : hasAccess ? (
              null
            ) : (
              <button
                onClick={handleRequestAccess}
                className="px-3 py-1 rounded bg-red-500 text-white text-xs hover:bg-red-600"
              >
                Request Access
              </button>
            )}
          </div>
          {app.plugin_type === 'WEB' && hasVersionChoice && (
            <div className="relative" ref={menuRef}>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowVersionMenu(!showVersionMenu);
                }}
                className={`px-3 py-1 rounded text-xs flex items-center space-x-2 ${isBetaActive ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20' : 'bg-gray-800 text-gray-300 border border-gray-700'}`}
              >
                <span>{isBetaActive ? 'Beta' : 'Production'}</span>
                <ChevronDown className="w-3.5 h-3.5"/>
              </button>
              {showVersionMenu && (
                <div className="absolute bottom-full right-0 mb-1 bg-gray-800 rounded shadow-lg border border-gray-700 overflow-hidden">
                  {hasProductionVersion && (
                    <button
                      onClick={(e) => { e.stopPropagation(); onVersionSwitch(app.id, 'production'); setShowVersionMenu(false); }}
                      className={`px-4 py-2 text-xs text-left w-full whitespace-nowrap ${!isBetaActive ? 'bg-gray-700 text-gray-200' : 'text-gray-300 hover:bg-gray-700'}`}
                    >
                      Production
                    </button>
                  )}
                  {hasBetaVersion && (
                    <button
                      onClick={(e) => { e.stopPropagation(); onVersionSwitch(app.id, 'beta'); setShowVersionMenu(false); }}
                      className={`px-4 py-2 text-xs text-left w-full whitespace-nowrap ${isBetaActive ? 'bg-amber-500/10 text-amber-400' : 'text-gray-300 hover:bg-amber-500/10'}`}
                    >
                      Beta
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AppCard;